import api from "./Api";
import { BASE_URL } from "./Context/config";
import Cookies from "js-cookie";

export interface PostUserData {
  email: string;
  firstName: string;
  lastName: string;
  userName: string;
  password: string;
  gender: string;
  categoryUser: string;
  profilePicture: string;
  profileCoverPicture: string;
}

export interface LogUserData {
  email: string;
  password: string;
}

const handleRandomPhotoSelect = async () => {
  const randomAvatarNumber = Math.floor(Math.random() * 24) + 1;
  const randomAvatarUrl = `/assets/images/avatars/avatar_${randomAvatarNumber}.jpg`;

  const response = await fetch(randomAvatarUrl);
  const blob = await response.blob();

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.readAsDataURL(blob);
  });
};

export const Register = async (user: PostUserData) => {
  const profilePicture =
    user.profilePicture || (await handleRandomPhotoSelect());
  const data = {
    Email: user.email,
    FirstName: user.firstName,
    LastName: user.lastName,
    UserName: user.userName,
    Password: user.password,
    Gender: user.gender,
    Category: user.categoryUser,
    ProfilePicture: profilePicture,
  };

  try {
    const response = await api.post(`${BASE_URL}/Auth/Register`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    localStorage.setItem("isEmailSent", "true");

    return response;
  } catch (error) {
    // First, try to extract the actual error message from backend
    if (error.response && error.response.data) {
      // Backend returns error as string directly
      if (typeof error.response.data === "string") {
        return { error: error.response.data };
      }
      // Backend returns error in error property
      if (error.response.data.error) {
        return { error: error.response.data.error };
      }
      // Backend returns error in message property
      if (error.response.data.message) {
        return { error: error.response.data.message };
      }
    }

    // If no specific error message, provide user-friendly fallbacks based on status codes
    if (error.response) {
      switch (error.response.status) {
        case 400:
          return {
            error:
              "Invalid registration data. Please check your information and try again.",
          };
        case 409:
          return {
            error: "An account with this email or username already exists.",
          };
        case 500:
          return {
            error: "Server error during registration. Please try again later.",
          };
        default:
          return { error: "Registration failed. Please try again." };
      }
    }
    return {
      error:
        "Network error. Please check your internet connection and try again.",
    };
  }
};

export const verifyEmail = async (token: string) => {
  try {
    const response = await api.get(`${BASE_URL}/Auth/VerifyEmail/${token}`);
    return response;
  } catch (error) {
    console.error("Error while verifying email:", error);
    return {
      error:
        "An error occurred while verifying the email. Please try again later.",
    };
  }
};

export const resendVerificationEmail = async (email: string) => {
  try {
    const response = await api.post(
      `${BASE_URL}/Auth/ResendVerificationEmail`,
      JSON.stringify(email),
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error: any) {
    console.error("Error while resending verification email:", error);
    if (error.response && error.response.data && error.response.data.error) {
      return { error: error.response.data.error };
    }
    return {
      error:
        "An error occurred while resending verification email. Please try again later.",
    };
  }
};

export const ForgotPassword = async (email: string) => {
  try {
    const response = await api.post(`${BASE_URL}/Auth/ForgotPassword`, email, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error: any) {
    if (error.response) {
      return error.response;
    }
    return {
      error: "An error occurred while forgot password. Please try again later.",
    };
  }
};

export const ChangePassword = async (password: string) => {
  try {
    const response = await api.post(
      `${BASE_URL}/Auth/ChangePassword`,
      password,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error while changing password:", error);
    // First, try to extract the actual error message from backend
    if (error.response && error.response.data) {
      // Backend returns error as string directly
      if (typeof error.response.data === "string") {
        return { error: error.response.data };
      }
      // Backend returns error in error property
      if (error.response.data.error) {
        return { error: error.response.data.error };
      }
      // Backend returns error in message property
      if (error.response.data.message) {
        return { error: error.response.data.message };
      }
    }

    // If no specific error message, provide user-friendly fallbacks based on status codes
    if (error.response) {
      switch (error.response.status) {
        case 400:
          return {
            error:
              "Invalid password format. Please check the requirements and try again.",
          };
        case 401:
          return { error: "Authentication failed. Please log in again." };
        case 500:
          return {
            error:
              "Server error while changing password. Please try again later.",
          };
        default:
          return { error: "Failed to change password. Please try again." };
      }
    }
    return {
      error:
        "Network error. Please check your internet connection and try again.",
    };
  }
};

export const VerifyPasswordChanging = async (
  confirmPassword: string,
  token: string
) => {
  try {
    const response = await api.post(
      `${BASE_URL}/Auth/VerifyPasswordChanging/${token}`,
      confirmPassword,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error: any) {
    console.error("Error while verifying changing password:", error);
    if (error.response) {
      return error.response;
    }
    return {
      status: 500,
      data: {
        error:
          "An error occurred while verifying changing password. Please try again later.",
      },
    };
  }
};

export const Login = async (user) => {
  const data = {
    Email: user.email,
    Password: user.password,
  };

  try {
    const response = await api.post(`/Auth/Login`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    const expiresIn = new Date(Date.parse(response.data.expiresIn));
    expiresIn.setHours(expiresIn.getHours() + 1);

    // Use centralized token setting function for consistency
    setAuthTokens(
      response.data.accessToken,
      response.data.refreshToken,
      expiresIn
    );

    return response;
  } catch (error) {
    // First, try to extract the actual error message from backend
    if (error.response && error.response.data) {
      // Backend returns error as string directly
      if (typeof error.response.data === "string") {
        return { error: error.response.data };
      }
      // Backend returns error in error property
      if (error.response.data.error) {
        return { error: error.response.data.error };
      }
      // Backend returns error in message property
      if (error.response.data.message) {
        return { error: error.response.data.message };
      }
    }

    // If no specific error message, provide user-friendly fallbacks based on status codes
    if (error.response) {
      switch (error.response.status) {
        case 401:
          return {
            error:
              "Invalid email or password. Please check your credentials and try again.",
          };
        case 403:
          return { error: "Access denied. Please verify your email address." };
        case 500:
          return { error: "Server error. Please try again later." };
        default:
          return {
            error: "Login failed. Please check your connection and try again.",
          };
      }
    }
    return {
      error:
        "Network error. Please check your internet connection and try again.",
    };
  }
};

export const Logout = async () => {
  clearAllTokens();
};

// Centralized function to clear all authentication tokens
export const clearAllTokens = () => {
  const cookieOptions = {
    path: "/",
    ...(process.env.REACT_APP_ENV === "production" && {
      domain: "contactie.com",
    }),
  };

  Cookies.remove("authToken", cookieOptions);
  Cookies.remove("refreshToken", cookieOptions);

  // Also clear any localStorage items related to authentication
  localStorage.removeItem("userCategory");
  localStorage.removeItem("isEmailSent");
  localStorage.removeItem("isCardVisible");
  localStorage.removeItem("isLinksCardVisible");
};

export const RefreshToken = async () => {
  const data = {
    refreshToken: getRefreshToken(),
  };

  try {
    const response = await api.post(`${BASE_URL}/Auth/RefreshToken`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    setAuthTokens(
      response.data.accessToken,
      response.data.refreshToken,
      response.data.expiresIn
    );

    return response;
  } catch (error) {
    return null;
  }
};

export function setAuthTokens(accessToken, refreshToken, expiresIn) {
  const expires = new Date(expiresIn);

  const cookieOptions = {
    expires: expires,
    path: "/",
    ...(process.env.REACT_APP_ENV === "production" && {
      domain: "contactie.com",
    }),
  };

  // Use js-cookie for consistent cookie handling
  Cookies.set("authToken", accessToken, cookieOptions);
  Cookies.set("refreshToken", refreshToken, cookieOptions);
}

// export function setAuthToken(token: string) {
//     const expires = new Date();
//     expires.setDate(expires.getDate() + 3);
//     document.cookie = `authToken=${token}; expires=${expires.toUTCString()}; path=/`;
// }

export function getRefreshToken() {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith("refreshToken=")) {
      const refreshToken = cookie.substring(
        "refreshToken=".length,
        cookie.length
      );
      if (refreshToken.length > 0) {
        return refreshToken;
      } else {
        console.error("Refresh token found, but it's empty or malformed.");
        return null;
      }
    }
  }

  return null;
}

export function checkAuthToken() {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith("authToken=")) {
      return true;
    }
  }

  return false;
}

// Utility function to get the current access token
export function getAccessToken() {
  return Cookies.get("authToken");
}

// Utility function to check if a token is expired (basic check)
export function isTokenExpired(token: string): boolean {
  if (!token) return true;

  try {
    // Decode JWT token to check expiration
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    // If we can't decode the token, consider it expired
    return true;
  }
}

// Centralized function to handle authentication failures
export const handleAuthenticationFailure = (
  navigate?: any,
  showToast?: any
) => {
  // Check if we're already on a public page to avoid unnecessary redirects
  const currentPath = window.location.pathname;
  const publicPaths = [
    "/Login",
    "/Signup",
    "/ForgotPasswordEmail",
    "/VerifyMail",
    "/VerifiyingMail",
    "/VerifyPasswordChanging",
    "/Profile",
    "/profile",
  ];
  const isOnPublicPage = publicPaths.some((path) =>
    currentPath.startsWith(path)
  );

  clearAllTokens();

  // Only show toast and redirect if we're not already on a public page
  if (!isOnPublicPage) {
    if (showToast) {
      showToast.error("Session expired. Please log in again.", {
        position: "top-center",
        autoClose: 3000,
      });
    }

    if (navigate) {
      navigate("/Login", { replace: true });
    } else {
      // Fallback to window.location if navigate is not available
      window.location.href = "/Login";
    }
  } else {
  }
};
